import { useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';
import {
  Box,
  Button,
  CircularProgress,
  Column,
  Flex,
  Grid,
  IconButton,
  Row,
  Screen,
} from '@tempmee/react';
import { OutlinePencilIcon, OutlineTrashIcon, OutlineXIcon } from '@tempmee/react-icons';
import history from '~/browserHistory';
import { BackButton } from '~/commonComponents';
import { useCancelJobMutation, useGetJob } from '~/domain/Jobs';
import { routes } from '~/routes';
import AnimatedCloseCircle from '../../shared/AnimatedCloseCircle';
import ConfirmPopup from '../../shared/ConfirmPopup';
import { InterviewInviteSection } from './InterviewInviteSection';
import { InviteSection } from './InviteSection';
import { ShiftDetailsInfo } from './ShiftDetailsInfo';

/**
 * Redesigned open Shift/Interview Details page. Two-column layout: the inline
 * invite/candidate list on the left, the read-only details on the right. Temp
 * open shifts show the professional invite list; perm onsite interviews show the
 * eligible-candidate list and hide Auto-Confirm. Gated by the
 * `ShiftDetailsInviteSection` experiment; the `control` variant renders the
 * legacy single-column screen.
 */
export default function OpenJobScreenRedesign() {
  const params = useParams<{ jobId?: string }>();

  const locationState = history.location.state as
    | { jobId?: string; isJobPost?: boolean }
    | undefined;
  const jobId = locationState ? locationState.jobId : params?.jobId;

  const { data: job = null, isLoading: isJobLoading } = useGetJob(jobId);
  const { mutate: cancelJob, isPending: isCancelPending } = useCancelJobMutation();
  const isLoading = isJobLoading || isCancelPending;

  const [cancelJobPopup, setCancelJobPopup] = useState(false);

  useEffect(() => {
    if (job && (job.status === 'pending' || job.status === 'counter')) {
      const candidateId = job?.multipleCounters?.[0]?.candidate?.id;
      history.replace(
        generatePath(routes.confirmCandidate, { jobId: job.id, candidateId: candidateId ?? '' }),
      );
    } else if (
      job &&
      (job.status === 'filled' || job.status === 'pay-out' || job.status === 'complete')
    ) {
      history.replace(generatePath(routes.jobDetails, { jobId: job.id }));
    }
  }, [job]);


  if (isLoading || !job) {
    return (
      <Screen variant="standard">
        <Screen.Body>
          <Flex justify="center" py={10}>
            <CircularProgress />
          </Flex>
        </Screen.Body>
      </Screen>
    );
  }

  const isInterview = job.isOnsiteInterview;

  // Edit routes to the same job-edit screen for both shifts and interviews,
  // matching the legacy flow (OpenJobScreen).
  const editPath = generatePath(routes.editOpenJob, { jobId: job.id });

  return (
    <>
      <Screen variant="standard">
        <Screen.Header
          start={
            <Box className="-ml-3">
              {locationState?.isJobPost ? (
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  startIcon={<OutlineXIcon />}
                  data-cy="shift-details-close"
                  onClick={() => history.push('/dashboard/home')}
                >
                  Close
                </Button>
              ) : (
                <BackButton />
              )}
            </Box>
          }
          title={isInterview ? 'Interview Details' : 'Shift Details'}
          end={
            <Row align="center" gap={10}>
              <IconButton
                type="button"
                color="primary"
                appearance="filled"
                size="medium"
                data-cy="edit-shift-button"
                aria-label="Edit"
                onClick={() => history.push(editPath)}
                Icon={<OutlinePencilIcon />}
              >
                Edit
              </IconButton>
              <IconButton
                type="button"
                color="primary"
                appearance="outline"
                size="medium"
                data-cy="cancel-shift-button"
                aria-label="Cancel"
                onClick={() => setCancelJobPopup(true)}
                Icon={<OutlineTrashIcon />}
              >
                Cancel
              </IconButton>
            </Row>
          }
        />
        <Screen.Body>
          <Flex justify="center" pt={16} pb={16}>
            <Box width="min(1200px, 100%)">
              <Grid columns="4fr 3fr" gap={16} align="start">
                {isInterview ? <InterviewInviteSection job={job} /> : <InviteSection job={job} />}
                <Column gap={10}>
                  <Box pt={isInterview ? 0 : 10}>
                    <ShiftDetailsInfo job={job} />
                  </Box>
                </Column>
              </Grid>
            </Box>
          </Flex>
        </Screen.Body>
      </Screen>

      {cancelJobPopup && (
        <ConfirmPopup
          Icon={() => <AnimatedCloseCircle />}
          title="Are you sure you want to cancel?"
          description={
            isInterview
              ? 'Cancelling an onsite interview means it will no longer be available to the candidates you invited.'
              : 'Cancelling an open shift means that it will no longer be available to professionals around your office to accept and potentially work.'
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setCancelJobPopup(false)}
          leftButtonDataCy="cancel-popup-go-back"
          rightButtonText={isInterview ? 'Cancel Interview' : 'Cancel Shift'}
          rightButtonDataCy="confirm-cancel-shift"
          rightButtonAction={() => {
            cancelJob({ jobId: job.id, redirect: true });
            setCancelJobPopup(false);
          }}
        />
      )}
    </>
  );
}
